import {useState} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Header from '../../support/Header';
import LoadingSpinner from '../../support/LoadingSpinner';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useAuthHeader } from '../../../hooks/useAuthHeader';
import axios from '../../../services/ApiService';
import Swal from 'sweetalert2'
import { BVTConstants } from '../../../utils/BVTConstants';
import './Login.css';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  contact?: string;
  legalName?: string;
  tradeName?: string;
  abn?: string;
  address?: string;
}

export default function Signup() {
  
    const [email, setEmail] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [contact, setContact] = useState('')
    const [legalName, setLegalName] = useState('')
    const [tradeName, setTradeName] = useState('')
    const [abn, setAbn] = useState('')
    const [address, setAddress] = useState('')
    const [otp, setOtp] = useState('')
    const [otpError, setOtpError] =useState('')
    const [password, setPassword] = useState('')
    const [passwordError, setPasswordError] = useState('')
    const [rePassword, setRePassword] = useState('')
    const [rePasswordError, setRePasswordError] = useState('')
    const [errors, setErrors] = useState<FormErrors>({})
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate()
    const {dispatch} = useAuthContext()
    const {setAuthHeader} = useAuthHeader();
  
    const validateUserDetails = () => {
      let formErrors: FormErrors = {};
      let isValid = true;

      if(firstName === '' || firstName.length < 3){
        formErrors.firstName = "First Name is required.";
        isValid = false;
      }

      if(lastName === '' || lastName.length < 3){
        formErrors.lastName = "Last Name is required.";
        isValid = false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        formErrors.email = "Email is required.";
        isValid = false;
      } else if (!emailRegex.test(email)) {
        formErrors.email = "Please enter a valid email address.";
        isValid = false;
      }

      if(legalName === '' || legalName.length < 3 || legalName.length > 30){
        formErrors.legalName = "Legal Name is required and must be between 3-30 characters.";
        isValid = false;
      }

      if(tradeName === '' || tradeName.length < 3 || tradeName.length > 30){
        formErrors.tradeName = "Trade Name is required and must be between 3-30 characters.";
        isValid = false;
      }

      if(abn === '' || abn.length !== 11 || !/^\d+$/.test(abn)){
        formErrors.abn = "ABN must be exactly 11 digits.";
        isValid = false;
      }

      if(address === '' || address.length < 3 || address.length > 30){
        formErrors.address = "Address is required and must be between 3-30 characters.";
        isValid = false;
      }

      if(contact === '' || contact.length < BVTConstants.PHONE_MIN_LENGTH || contact.length > BVTConstants.PHONE_MAX_LENGTH){
        formErrors.contact = `Contact number is required and must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} digits.`;
        isValid = false;
      }

      setErrors(formErrors);
      console.log(errors);
      return isValid;
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        console.log('inside submit.')
        setPasswordError('');
        setRePasswordError('');
        setOtpError('');
        if(validateUserDetails()){
          let isValid = true;
          if (!password) {
            setPasswordError("Password is required.");
            isValid=false;
          } else if (password.length < 6) {
            setPasswordError("Password must be at least 6 characters.");
            isValid=false;
          }

          if(!rePassword || password !== rePassword){
            setRePasswordError("Passwords must match.");
            isValid=false;
          }

          if(!otp){
            setOtpError("OTP is required.");
            isValid=false;
          }

          if(isValid){
            console.log('singing up')
            const user = {
              firstName: firstName,
              lastName: lastName,
              email: email,
              contact: contact,
              legalName: legalName,
              tradeName: tradeName,
              abn: abn,
              address: address,
              role: BVTConstants.USER_TYPE_BUSINESS,
              password: password,
              otp: otp
            }
            try {
              setLoading(true);
              await axios.post("/validate-mail-otp", user);
              const response = await axios.post("/register", user);
              
              setAuthHeader(response.data.token);
              dispatch({ type: 'LOGIN', payload: response.data });
              setLoading(false);
              Swal.fire({ title: 'Success!', text: 'User Created Successfully.', icon: 'success', confirmButtonText: 'Ok' })
              navigate('/', {replace: true});
          } catch (error:any) {
              setLoading(false);
              if (error.status === 400) {
                setOtpError("Invalid OTP.")
              } else {
                Swal.fire({title: 'Failed!', text: 'Could Not Complete Signup, Please Try Again!', icon: 'error', confirmButtonText: 'Ok' })
              }
          }
        }
      }
    }

    const handleSendOtp = async () => {
      if(validateUserDetails()){
            const data = {
                firstName: firstName,
                lastName: lastName,
                email: email,
                contact: contact,
                legalName: legalName,
                tradeName: tradeName,
                abn: abn,
                address: address
            }
            setLoading(true);
            try {
              await axios.post("/generate-mail-otp", data);
              setLoading(false);
              Swal.fire({ title: 'Success!', text: 'Otp Sent Successfully!', icon: 'success', confirmButtonText: 'OK' })
            } catch (error) {
                setLoading(false);
                Swal.fire({ title: 'Failed!', text: 'Error While Sending OTP, Please try again after sometime.', icon: 'error', confirmButtonText: 'OK' })  
            }
        }
    }

    return (
      <>
      <Helmet><title>BVT - Sign Up</title></Helmet>
        <div className='signup-body'>
          <Header login={true}/>
          <div className="login-container">
            <form className="login-form" onSubmit={handleSubmit}>
              <h2 className="form-title">Business Sign Up</h2>
              <div className="form-group">
                <label htmlFor="legalName">Legal Name</label>
                <input
                  type="text"
                  id="legalName"
                  className="form-input"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  maxLength={30}
                />
                {errors.legalName && <p className="error">{errors.legalName}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="tradeName">Trade Name</label>
                <input
                  type="text"
                  id="tradeName"
                  className="form-input"
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                  maxLength={30}
                />
                {errors.tradeName && <p className="error">{errors.tradeName}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="abn">ABN</label>
                <input
                  type="text"
                  id="abn"
                  className="form-input"
                  value={abn}
                  onChange={(e) => setAbn(e.target.value.replace(/\D/g, ''))}
                  maxLength={11}
                  placeholder="11 digits"
                />
                {errors.abn && <p className="error">{errors.abn}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  className="form-input"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  maxLength={30}
                />
                {errors.address && <p className="error">{errors.address}</p>}
              </div>
              <div className="contact-details-section">
                <h3 className="section-title">Contact Person Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      className="form-input"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    {errors.firstName && <p className="error">{errors.firstName}</p>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      className="form-input"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      
                    />
                    {errors.lastName && <p className="error">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="contact">Contact Number</label>
                  <input
                    type="tel"
                    id="contact"
                    className="form-input"
                    value={contact}
                    onChange={(e) => setContact(e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, ''))}
                    maxLength={BVTConstants.PHONE_MAX_LENGTH}
                    placeholder="10-15 digits"
                  />
                  {errors.contact && <p className="error">{errors.contact}</p>}
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    
                  />
                  {errors.email && <p className="error">{errors.email}</p>}
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  
                />
                {passwordError && <p className="error">{passwordError}</p>}
              </div>
              <div className="form-group">
                  <label htmlFor="rePassword">Confirm Password</label>
                  <input
                    type="password"
                    id="rePassword"
                    className="form-input"
                    value={rePassword}
                    onChange={(e) => setRePassword(e.target.value)}
                    
                  />
                  {rePasswordError && <p className="error">{rePasswordError}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="otp">OTP</label>
                <div className='otp-group'>
                  <input
                    type="text"
                    id="otp"
                    className="form-input otp-input"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <button type="button" className="otp-btn" onClick={handleSendOtp}>
                    Send OTP
                  </button>
                </div>
                {otpError && <p className="error">{otpError}</p>}
              </div>
              <button type="submit" className="submit-btn">Sign Up</button>
            </form> 
        </div>
        <LoadingSpinner loading={loading}/>
      </div>
    </>
    )
}